import { createContext, useContext, useState, useEffect } from "react";
import { TENANTS, DEFAULT_TENANT, fetchTenantConfig } from "@/config/tenants";

const TenantContext = createContext(null);

function detectSlug() {
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    const p = new URLSearchParams(window.location.search);
    return p.get("tenant") || null;
  }
  const parts = hostname.split(".");
  if (parts.length >= 3) {
    const sub = parts[0];
    if (sub === "app" || sub === "www") return null;
    return sub;
  }
  return null;
}

function applyTenantTheme(t) {
  const root = document.documentElement;
  const set  = (k, v) => root.style.setProperty(k, v);
  set("--brand",       t.brand);
  set("--brand-hover", t.brandHover);
  set("--brand-muted", t.brandMuted);
  set("--bg",          t.bg);
  set("--text-primary",   t.textPrimary);
  set("--text-secondary", t.textSecondary);
  set("--text-muted",     t.textMuted);
  set("--border-soft",    t.borderSoft);
  const m = (sel, attr, val) => {
    const el = document.querySelector(sel);
    if (el) el.setAttribute(attr, val);
  };
  m('meta[name="theme-color"]', "content", t.themeColor);
  m('meta[name="description"]', "content", t.description);
  m('link[rel="icon"]', "href", t.logo);
  m('link[rel="apple-touch-icon"]', "href", t.logo);
  document.title = `Stock — ${t.name}`;
}

const BRAND_DEFAULTS = {
  brand: "#6366F1", brandHover: "#4F46E5", brandMuted: "#EEF2FF",
  bg: "#FAFAFA", textPrimary: "#18181B", textSecondary: "#3F3F46",
  textMuted: "#71717A", borderSoft: "#E4E4E7", themeColor: "#6366F1",
  logo: "/logos/default.png",
  description: "Gestion des stocks — Quantelys",
};

function mergeTenantConfig(backendConfig) {
  const s = backendConfig.settings || {};
  return {
    slug: backendConfig.slug,
    backendSlug: backendConfig.slug,
    name: backendConfig.name,
    shortName: backendConfig.name,
    ...BRAND_DEFAULTS,
    ...s,
    logo: backendConfig.logo_url || s.logo || BRAND_DEFAULTS.logo,
    themeColor: s.brand || BRAND_DEFAULTS.themeColor,
    description: `Gestion des stocks — ${backendConfig.name}`,
  };
}

/**
 * TenantProvider renders instantly with the hardcoded fallback.
 * Backend config is fetched in the background and merged when available.
 * NEVER blocks rendering — children always render immediately.
 */
export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(() => {
    // Fast path: pre-initialize from hardcoded config before even mounting
    const slug = detectSlug();
    const hardcoded = slug ? TENANTS[slug] : null;
    const initial = hardcoded || { ...DEFAULT_TENANT, isRootDomain: !slug };
    return initial;
  });

  // Async: merge backend config once mounted (non-blocking)
  useEffect(() => {
    const slug = detectSlug();
    if (!slug) return; // root domain — already set above

    const hardcoded = TENANTS[slug];
    const backendSlug = hardcoded?.backendSlug || slug;

    fetchTenantConfig(backendSlug).then((result) => {
      if (!result.found) return; // unknown tenant, keep hardcoded
      const merged = mergeTenantConfig(result);
      const final = hardcoded
        ? {
            ...hardcoded,
            name: merged.name,
            logo: merged.logo !== BRAND_DEFAULTS.logo ? merged.logo : hardcoded.logo,
          }
        : merged;
      applyTenantTheme(final);
      setTenant({ ...final, isRootDomain: false });
    }).catch(() => {
      // network error — keep hardcoded, already rendered
    });
  }, []);

  // Apply initial theme immediately (hardcoded is already set above)
  useEffect(() => {
    applyTenantTheme(tenant);
  }, []); // run once on mount with initial tenant

  return (
    <TenantContext.Provider value={tenant}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used within TenantProvider");
  return ctx;
}